
import { Component, Vue } from "vue-property-decorator";
import PModal from "@/components/look/PModal.vue";
import { BModal } from "bootstrap-vue";
import { IRequestHistorialsService, ISolCustomerServiceRating, IUserDetailData } from "@/entities";
import VueFilters from "@/vue-filters";
import { i18n } from "@/i18n";
import { Action, Getter } from "vuex-class";
import {
  ISolCustomerGetServiceParams,
  ISolCustomerResponse,
  ISolCustomerServiceRatingResponse,
} from "@/services";
import { ValidationObserver, ValidationProvider } from "vee-validate";

@Component({
  name: "rating-order-modal",
  mixins: [VueFilters],
  components: {
    PModal,
    ValidationObserver,
    ValidationProvider,
  },
})
export default class RatingOrderModal extends Vue {
  @Getter("getLoggedUser") getLoggedUser!: IUserDetailData;
  @Action("fetchServiceRating") fetchServiceRating!: (
    params: ISolCustomerGetServiceParams
  ) => Promise<ISolCustomerServiceRatingResponse | undefined>;
  @Action("saveServiceRating") saveServiceRating!: (
    serviceRating: ISolCustomerServiceRating
  ) => Promise<ISolCustomerResponse | undefined>;
  ratingValue: number | null = 0;
  comments = "";
  serviceId: string | undefined;
  serviceCode: string | undefined = "";
  serviceType: number | undefined | null = null;
  serviceTypeDesc: string | undefined = "";
  serviceDate = "";
  readonly = false;

  constructor() {
    super();
  }

  async mounted(): Promise<void> {
    this.$on("showRatingOrderModal", (data: IRequestHistorialsService) => {
      this.showModal(data);
    });
  }

  async beforeDestroy(): Promise<void> {
    this.$off("showRatingOrderModal");
  }

  async showModal(data: IRequestHistorialsService): Promise<void> {
    this.serviceType = data.serviceType;
    this.serviceId = data.serviceId;
    this.serviceCode = data.serviceCode;
    this.serviceDate = data.realInitialTime || "";
    this.ratingValue = null;
    this.comments = "";
    this.readonly = false;

    if (data.productCode && data.productDescription) {
      this.serviceTypeDesc = i18n.t(data.productDescription).toString();
    }

    switch (this.serviceType) {
      case 0:
        this.serviceTypeDesc = i18n.t("label.pickup").toString();
        break;
      case 1:
        if (data.isChange) {
          this.serviceTypeDesc = i18n.t("label.deliveryExchange").toString();
        } else {
          this.serviceTypeDesc = i18n.t("label.delivery").toString();
        }
        break;
      case 3:
        this.serviceTypeDesc = i18n.t("label.technicalAssistance").toString();
        break;
      case 4:
        this.serviceTypeDesc = i18n.t("label.supplyMaterialService").toString();
        break;
    }

    if (data.serviceRateId) {
      this.readonly = true;
      const params = {
        countryCode: this.getLoggedUser.country?.code || "",
        serviceId: data.serviceRateId,
      } as ISolCustomerGetServiceParams;

      const res = await this.fetchServiceRating(params);

      this.ratingValue = res?.serviceRating?.rate || null;
      this.comments = res?.serviceRating?.comments || "";
    }

    const pModal = this.$refs.orderRatingModal as PModal;
    const bModal = pModal.$children[0] as BModal;
    bModal.show();
  }

  hideModal(): void {
    const pModal = this.$refs.orderRatingModal as PModal;
    const bModal = pModal.$children[0] as BModal;
    bModal.hide();
  }

  get ratingLabel(): string {
    let text = "";
    switch (this.ratingValue) {
      case 1:
        text = "label.veryBad";
        break;
      case 2:
        text = "label.bad";
        break;
      case 3:
        text = "label.regular";
        break;
      case 4:
        text = "label.good";
        break;
      case 5:
        text = "label.excellent";
        break;
    }
    return text;
  }

  makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }

  async onSubmit(): Promise<void> {
    const serviceRating = {
      countryCode: this.getLoggedUser.country?.code,
      serviceId: this.serviceId,
      rate: this.ratingValue,
      comments: this.comments,
    } as ISolCustomerServiceRating;

    const res = await this.saveServiceRating(serviceRating);

    if (res) {
      this.makeToast("success", i18n.t("messages.rateOrder.ok").toString());
      this.$emit("success");
      this.hideModal();
    } else {
      this.makeToast("warning", i18n.t("messages.rateOrder.ko").toString());
    }
  }
}
